// Shared payee-address extraction for the EFT / Check / Debit payment reports.
// The original code used deeply nested try/catch blocks to fall back from
// claim_data.providers.provider.address to claim_data.shipping_info.address;
// that behaviour (including returning null/undefined when a field exists but is
// empty) is preserved exactly.

function safe(fn) {
    try {
        return fn();
    } catch {
        return "";
    }
}

function safeWithFallback(primary, fallback) {
    return safe(() => {
        try {
            return primary();
        } catch {
            return fallback();
        }
    });
}

/**
 * @param {object} claim_data
 * @param {object} [options]
 * @param {boolean} [options.coalesceShippingAddress2] Quirk preserved from the
 *   xlsx export path only: when the shipping fallback is used, a falsy
 *   address_2 becomes "" instead of being passed through as-is.
 */
export function getPayeeParts(claim_data, { coalesceShippingAddress2 = false } = {}) {
    const provider = () => claim_data.providers.provider;
    const shippingAddress = () => claim_data.shipping_info.address;

    return {
        providerFirst: safe(() => provider().first_name),
        providerLast: safe(() => provider().last_name),
        address_1: safeWithFallback(
            () => provider().address.address_1,
            () => shippingAddress().address_1
        ),
        address_2: safeWithFallback(
            () => provider().address.address_2,
            () => (coalesceShippingAddress2 ? shippingAddress().address_2 || "" : shippingAddress().address_2)
        ),
        city: safeWithFallback(
            () => provider().address.city,
            () => shippingAddress().city
        ),
        state: safeWithFallback(
            () => provider().address.state,
            () => shippingAddress().state
        ),
        zipcode: safeWithFallback(
            () => provider().address.zip_code,
            () => shippingAddress().zip_code
        ),
    };
}

/** Flat "addr1 addr2 city state zip" string used by the xlsx exports. */
export function formatPayeeAddress(claim_data) {
    const { address_1, address_2, city, state, zipcode } = getPayeeParts(claim_data, { coalesceShippingAddress2: true });
    return address_1 + " " + address_2 + " " + city + " " + state + " " + zipcode;
}


